import { useState, useCallback } from 'react';
import { crumbChatService } from '../services/crumbChatService';

/**
 * React hook for managing Crumb chat functionality
 */
export const useCrumbChat = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState(null);
  const [useStreaming, setUseStreaming] = useState(true);

  // Initialize the chat service
  const initializeChat = useCallback(async () => {
    try {
      if (!isInitialized) {
        await crumbChatService.initialize();
        setIsInitialized(true);
        
        // Add welcome message from Crumb
        addMessage({
          content: "Hi! I'm Crumb, your frosting-covered assistant! 🧁💖",
          sender: 'crumb',
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.error('🧁 Error initializing chat:', error);
      addMessage({
        content: "I-I'm having trouble loading my knowledge... 🧁 Please try again later!",
        sender: 'crumb',
        timestamp: Date.now()
      });
    }
  }, [isInitialized]);

  // Add a message to the chat
  const addMessage = useCallback((message) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      timestamp: Date.now(),
      isStreaming: false,
      ...message
    };
    
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  // Update a streaming message
  const updateStreamingMessage = useCallback((messageId, chunk) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          content: msg.content + chunk
        };
      }
      return msg;
    }));
    setCurrentStreamingMessageId(messageId);
  }, []);

  // Finish streaming a message
  const finishStreamingMessage = useCallback((messageId) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          isStreaming: false
        };
      }
      return msg;
    }));
    setCurrentStreamingMessageId(null);
  }, []);

  // Send a message to Crumb
  const sendMessage = useCallback(async (userMessage) => {
    if (!isInitialized) {
      await initializeChat();
    }

    // Add user message
    addMessage({
      content: userMessage,
      sender: 'user'
    });

    setIsTyping(true);

    try {
      if (useStreaming) {
        // Add empty message for streaming
        const messageId = addMessage({
          content: '',
          sender: 'crumb',
          isStreaming: true
        });

        // Stream the response
        await crumbChatService.sendMessageStreaming(
          userMessage,
          (chunk) => {
            updateStreamingMessage(messageId, chunk);
          }
        );

        finishStreamingMessage(messageId);
      } else {
        // Non-streaming response
        const response = await crumbChatService.sendMessage(userMessage);
        addMessage({
          content: response.content,
          sender: 'crumb'
        });
      }
    } catch (error) {
      console.error('🧁 Error sending message:', error);
      addMessage({
        content: "I-I'm having trouble thinking right now... 🧁 Can you try again? Please don't give up on me!",
        sender: 'crumb'
      });
    } finally {
      setIsTyping(false);
    }
  }, [isInitialized, useStreaming, initializeChat, addMessage, updateStreamingMessage, finishStreamingMessage]);

  // Clear chat history
  const clearMessages = useCallback(() => {
    setMessages([]);
    crumbChatService.clearHistory();
    
    // Add welcome message again
    addMessage({
      content: "Hi! I'm Crumb, your frosting-covered assistant! 🧁💖",
      sender: 'crumb',
      timestamp: Date.now()
    });
  }, [addMessage]);

  return {
    messages,
    isTyping,
    isInitialized,
    currentStreamingMessageId,
    useStreaming,
    setUseStreaming,
    initializeChat,
    sendMessage,
    clearMessages,
    addMessage,
    updateStreamingMessage,
    finishStreamingMessage
  };
};
